/**
 * Pagina gestione posizioni di magazzino.
 *
 * Mostra la lista delle posizioni con aggiunta rapida inline,
 * modifica inline e soft delete. Segue lo stesso pattern di
 * CatalogoTable nel modulo fornitori.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import { Check, Pencil, Trash2, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';

interface Posizione {
  id: string;
  codice: string;
  descrizione: string | null;
  stato: string;
}

/** Headers comuni per le fetch autenticate. */
const authHeaders = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
  'Content-Type': 'application/json',
});

/**
 * Pagina posizioni con aggiunta rapida, modifica inline e soft delete.
 */
const PosizioniList: React.FC = () => {
  const [posizioni, setPosizioni] = useState<Posizione[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Form aggiunta rapida
  const [showForm, setShowForm] = useState(false);
  const [newCodice, setNewCodice] = useState('');
  const [newDescrizione, setNewDescrizione] = useState('');
  const [saving, setSaving] = useState(false);

  // Modifica inline
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCodice, setEditCodice] = useState('');
  const [editDescrizione, setEditDescrizione] = useState('');

  const baseUrl = '/api/v1/magazzino/posizioni';

  const carica = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(baseUrl, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
        },
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: Posizione[] = await response.json();
      setPosizioni(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  /** Crea una nuova posizione. */
  const handleAdd = async () => {
    if (!newCodice.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        codice: newCodice.trim(),
        descrizione: newDescrizione.trim() || null,
      };
      const response = await fetch(baseUrl, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setNewCodice('');
      setNewDescrizione('');
      setShowForm(false);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Entra in modalità modifica su una riga. */
  const startEdit = (item: Posizione) => {
    setEditingId(item.id);
    setEditCodice(item.codice);
    setEditDescrizione(item.descrizione ?? '');
  };

  /** Salva la modifica inline con PATCH. */
  const handleSaveEdit = async () => {
    if (!editingId || !editCodice.trim()) return;
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        codice: editCodice.trim(),
        descrizione: editDescrizione.trim() || null,
      };
      const response = await fetch(`${baseUrl}/${editingId}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      setEditingId(null);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  /** Soft delete di una posizione. */
  const handleDelete = async (itemId: string) => {
    try {
      const response = await fetch(`${baseUrl}/${itemId}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      void carica();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore eliminazione');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
        Posizioni di magazzino
      </h1>

      <Card>
        <CardContent className="p-0">
          {loading && (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Caricamento…
            </p>
          )}
          {error && (
            <p className="p-6 text-center text-sm text-destructive">{error}</p>
          )}
          {!loading && !error && (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Codice</TableHead>
                    <TableHead>Descrizione</TableHead>
                    <TableHead>Stato</TableHead>
                    <TableHead className="w-24" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posizioni.length === 0 && !showForm && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        className="text-center text-sm text-muted-foreground"
                      >
                        Nessuna posizione configurata
                      </TableCell>
                    </TableRow>
                  )}
                  {posizioni.map((pos: Posizione) =>
                    editingId === pos.id ? (
                      <TableRow key={pos.id}>
                        <TableCell>
                          <Input
                            value={editCodice}
                            onChange={(e) => setEditCodice(e.target.value)}
                            className="h-8 w-32 font-mono text-sm"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={editDescrizione}
                            onChange={(e) => setEditDescrizione(e.target.value)}
                            className="h-8 w-48"
                          />
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              pos.stato === 'attivo' ? 'default' : 'secondary'
                            }
                          >
                            {pos.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={handleSaveEdit}
                              disabled={saving}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => setEditingId(null)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow key={pos.id}>
                        <TableCell className="font-mono text-sm">
                          {pos.codice}
                        </TableCell>
                        <TableCell>{pos.descrizione ?? '—'}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              pos.stato === 'attivo' ? 'default' : 'secondary'
                            }
                          >
                            {pos.stato === 'attivo' ? 'Attivo' : 'Disattivato'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => startEdit(pos)}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive hover:text-destructive"
                              onClick={() => handleDelete(pos.id)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>

              {/* Form aggiunta rapida */}
              {showForm && (
                <div className="flex flex-wrap items-end gap-3 border-t p-4">
                  <Input
                    placeholder="Codice"
                    value={newCodice}
                    onChange={(e) => setNewCodice(e.target.value)}
                    className="w-32"
                  />
                  <Input
                    placeholder="Descrizione"
                    value={newDescrizione}
                    onChange={(e) => setNewDescrizione(e.target.value)}
                    className="w-48"
                  />
                  <Button size="sm" onClick={handleAdd} disabled={saving}>
                    {saving ? 'Salva…' : 'Aggiungi'}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setShowForm(false)}
                  >
                    Annulla
                  </Button>
                </div>
              )}

              {!showForm && (
                <div className="border-t p-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowForm(true)}
                  >
                    Aggiungi posizione
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PosizioniList;
